import { DragEvent, ReactNode, useState } from 'react';
import { Link } from 'react-router-dom';
import { Thumbnail } from './ui';
import { ViewMode } from './list-controls';

/**
 * Renders one video (or playlist) as either a list row or a grid tile depending on `viewMode` —
 * the single shared presentation used by the library, shared library, and playlist pages, so
 * sort/search/quick-actions all look and behave the same everywhere. Returns an `<li>` in list
 * mode (callers render inside a `<ul>`) and a `<div>` in grid mode (callers render inside a
 * `.item-grid` container) — see library-page.tsx etc. for the wrapping.
 *
 * Rename is handled directly here (rather than via the generic `actions` slot) because it needs
 * to swap the title display itself into an inline input — every other quick action (share,
 * delete, unshare, remove) only ever needs a click handler, so those are passed in as `actions`
 * children (see quick-actions.tsx) composed per-page.
 */
export function ItemCard({
  href,
  thumbnailUrl,
  title,
  viewMode,
  meta,
  actions,
  onRename,
  hideThumbnail,
  dragHandle,
  draggable,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
  selectionMode,
  checkbox,
}: {
  href: string;
  thumbnailUrl?: string;
  title: string;
  viewMode: ViewMode;
  meta?: ReactNode;
  actions?: ReactNode;
  onRename?: (next: string) => Promise<void>;
  /** Skips rendering the thumbnail entirely — for items with no natural single thumbnail (e.g.
   * playlists), rather than showing a permanently-empty placeholder box. */
  hideThumbnail?: boolean;
  /** A visual grab affordance rendered in the actions row (e.g. a drag-handle icon) — purely
   * decorative, since the draggable surface is the whole card, not this element specifically. */
  dragHandle?: ReactNode;
  draggable?: boolean;
  onDragStart?: (e: DragEvent) => void;
  onDragOver?: (e: DragEvent) => void;
  onDrop?: (e: DragEvent) => void;
  onDragEnd?: () => void;
  /** While the page is in bulk-selection mode, the actions row shows only `checkbox` (if this
   * item is selectable) instead of rename/actions/drag — every other quick action is suspended
   * for the duration, matching design.md's "selection mode swaps icons for a checkbox" decision. */
  selectionMode?: boolean;
  /** Omit even while `selectionMode` is on to render no checkbox at all for an item the viewer
   * isn't permitted to include in the bulk action (see the bulk-selection spec's permission rule). */
  checkbox?: { checked: boolean; onChange: () => void };
}) {
  const [renaming, setRenaming] = useState(false);
  const [name, setName] = useState(title);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState(false);

  const submitRename = async () => {
    const trimmed = name.trim();
    if (!trimmed || trimmed === title) {
      setRenaming(false);
      return;
    }
    setPending(true);
    setError(false);
    try {
      await onRename!(trimmed);
      setRenaming(false);
    } catch {
      setError(true);
    } finally {
      setPending(false);
    }
  };

  const titleNode = renaming ? (
    <form
      style={{ display: 'flex', gap: 4 }}
      onSubmit={(e) => {
        e.preventDefault();
        submitRename();
      }}
    >
      <input
        value={name}
        onChange={(e) => setName(e.target.value)}
        autoFocus
        style={{ flex: 1, minWidth: 0 }}
      />
      <button
        type="submit"
        className="icon-button"
        disabled={pending}
        aria-label="Save name"
        title="Save name"
      >
        ✅
      </button>
      <button
        type="button"
        className="icon-button"
        aria-label="Cancel rename"
        title="Cancel rename"
        onClick={() => setRenaming(false)}
      >
        ❌
      </button>
    </form>
  ) : (
    <Link to={href} className={viewMode === 'grid' ? 'item-card-title' : undefined}>
      <strong>{title}</strong>
    </Link>
  );

  const renameButton = onRename && !renaming && (
    <button
      className="icon-button"
      aria-label="Rename"
      title="Rename"
      onClick={() => {
        setName(title);
        setError(false);
        setRenaming(true);
      }}
    >
      ✏️
    </button>
  );

  const checkboxNode = checkbox && (
    <input
      type="checkbox"
      aria-label={`Select ${title}`}
      checked={checkbox.checked}
      onChange={checkbox.onChange}
      style={{ width: 20, height: 20 }}
    />
  );

  const actionsRow = selectionMode ? (
    // Deliberately NOT .item-card-actions — that class is opacity:0 until hover/focus (by design,
    // for the rename/share/delete icons), which would make a checked checkbox disappear the
    // moment the pointer left the row. A selection checkbox must stay visible regardless.
    checkboxNode && <div className="item-card-select">{checkboxNode}</div>
  ) : (
    (renameButton || actions || dragHandle) && (
      <div className="item-card-actions">
        {dragHandle}
        {renameButton}
        {actions}
      </div>
    )
  );

  if (viewMode === 'grid') {
    return (
      <div
        className="card item-card-grid"
        draggable={draggable}
        onDragStart={onDragStart}
        onDragOver={onDragOver}
        onDrop={onDrop}
        onDragEnd={onDragEnd}
      >
        <div className="item-card-thumb-wrap">
          {!hideThumbnail && <Thumbnail src={thumbnailUrl} alt={title} size="lg" />}
          {actionsRow}
        </div>
        {titleNode}
        {meta && <div className="muted item-card-meta">{meta}</div>}
        {error && <span className="error">Couldn't rename.</span>}
      </div>
    );
  }

  return (
    <li
      className="card list-row item-card-list"
      style={{ marginBottom: 'var(--space-3)', justifyContent: 'space-between' }}
      draggable={draggable}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
      onDragEnd={onDragEnd}
    >
      <div className="list-row" style={{ flex: 1, minWidth: 0 }}>
        {!hideThumbnail && <Thumbnail src={thumbnailUrl} alt={title} />}
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ marginBottom: 4 }}>{titleNode}</div>
          {meta}
          {error && (
            <div>
              <span className="error">Couldn't rename.</span>
            </div>
          )}
        </div>
      </div>
      {actionsRow}
    </li>
  );
}
